import {
  useMediaQuery,
  WhmAlert,
  WhmBox,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmDivider,
  WhmDropzone,
  WhmGrid,
  WhmIconButton,
  WhmInputAdornment,
  WhmLoadingButton,
  WhmSkeleton,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";
import {
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import CountryTextField from "../../../shared/CountryTextField/CountryTextField";
import { ReactComponent as OpenEye } from "@securitize/assets-sec-ui/dist/icons/svg/icon-eye-show.svg";
import { ReactComponent as ClosedEye } from "@securitize/assets-sec-ui/dist/icons/svg/icon-eye-hide.svg";
import { ReactComponent as InfoIcon } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import { updateKeyParty } from "../../../../data/api";
import { useQueryClient } from "@tanstack/react-query";
import { SnackbarContext } from "../../../shared/Snackbar";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import { ResponseError } from "../../../../data/types/responseError";
import { allowedTaxIdCharsRegex } from "../../../../utils/validations";
import { getSensitiveFieldMaskProps } from "../../../../utils/maskUtils";
import { ReactComponent as CheckMark } from "@securitize/assets-sec-ui/dist/icons/svg/check.svg";
import { KeyParty } from "../../../../data/dtos/get-key-party.dto";
import {
  useRequiredKeyPartyDocumentsQuery,
  useUploadInvestorDocumentTempMutation,
  useGetKeyPartyDocumentsQuery,
  useAddKeyPartyDocumentMutation,
  useDeleteKeyPartyDocumentMutation,
} from "../../../../data/useProfile";
import { getDocumentTypeTranslationKeys } from "../../../../contexts/I18nContext/translationKeys";
import {
  DocumentState,
  DocumentStatuses,
  TempDocument,
} from "../../documents/interfaces/documents-interfaces";
import { invalidateKeyPartiesQueries } from "../../../../utils/queryInvalidation";

type FormType = { keyParty: KeyParty };

export default function CompleteEntityDialog({
  open,
  onClose,
  keyParty,
  readOnly = false,
  flaggedComment,
}: {
  open: boolean;
  keyParty?: KeyParty;
  onClose: () => void;
  readOnly?: boolean;
  flaggedComment?: string;
}) {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const [displayTopDivider, setDisplayTopDivider] = useState(false);
  const [displayBottomDivider, setDisplayBottomDivider] = useState(false);
  const { getTranslation } = useContext(I18nContext);

  const dialogContentRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const snackbarContext = useContext(SnackbarContext);
  const addKeyPartyDocumentMutation = useAddKeyPartyDocumentMutation();
  const [tempDocuments, setTempDocuments] = useState<Map<string, TempDocument>>(
    new Map(),
  );

  const handleClose = () => {
    setTempDocuments(new Map());
    onClose();
  };

  return (
    <WhmDialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? "xl" : "md"}
      TransitionProps={{
        onEnter: () => {
          if (dialogContentRef.current) {
            if (
              dialogContentRef.current.scrollHeight >
              dialogContentRef.current.clientHeight
            ) {
              setDisplayBottomDivider(true);
            }
          }
        },
      }}
    >
      <Formik<FormType>
        initialValues={{
          keyParty: keyParty!,
        }}
        onSubmit={async (values, helpers) => {
          helpers.setSubmitting(true);

          try {
            // First, upload all temp documents to permanent storage
            for (const [documentType, tempDoc] of Array.from(
              tempDocuments.entries(),
            )) {
              await addKeyPartyDocumentMutation.mutateAsync({
                keyPartyId: keyParty!.id,
                documentType,
                documentCategory: "signer-entity",
                fileName: tempDoc.fileName,
                fileType: tempDoc.fileType,
                side: "front",
              });
            }

            // Then update key party
            await updateKeyParty(keyParty!.id, values.keyParty);
            await invalidateKeyPartiesQueries(queryClient);

            snackbarContext.showSnackbar({
              message: (
                <WhmStack direction="row" alignItems="center" gap="16px">
                  <CheckMark fill="white" />
                  <Translate
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_UPDATE_SUCCESS
                    }
                    replacements={{
                      keyPartyName: keyParty?.displayName ?? "",
                    }}
                  />
                </WhmStack>
              ),
              severity: "info",
            });

            handleClose();
          } catch (error) {
            snackbarContext.showSnackbar({
              message: <ErrorSnackbar error={error as ResponseError} />,
              severity: "error",
            });
          } finally {
            helpers.setSubmitting(false);
          }
        }}
        validationSchema={yup.object().shape({
          keyParty: yup.object().shape({
            taxCountryCode: yup.string(),
            taxId: yup.string().matches(allowedTaxIdCharsRegex, {
              message: getTranslation(
                translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_TAX_ID_INVALID,
              ),
            }),
            email: yup
              .string()
              .email(
                getTranslation(
                  translationKeys.PROFILE_KEY_PARTIES_VALIDATION_EMAIL,
                ),
              ),
            doingBusinessAs: yup.string(),
          }),
        })}
      >
        <CompleteEntityDialogContent
          onClose={handleClose}
          displayTopDivider={displayTopDivider}
          displayBottomDivider={displayBottomDivider}
          setDisplayTopDivider={setDisplayTopDivider}
          setDisplayBottomDivider={setDisplayBottomDivider}
          tempDocuments={tempDocuments}
          setTempDocuments={setTempDocuments}
          readOnly={readOnly}
          flaggedComment={flaggedComment}
        />
      </Formik>
    </WhmDialog>
  );
}

const CompleteEntityDialogContent = forwardRef(
  function CompleteEntityDialogContentFn(
    {
      onClose,
      displayTopDivider,
      displayBottomDivider,
      setDisplayTopDivider,
      setDisplayBottomDivider,
      tempDocuments,
      setTempDocuments,
      readOnly = false,
      flaggedComment,
    }: {
      onClose: () => void;
      displayTopDivider: boolean;
      displayBottomDivider: boolean;
      setDisplayTopDivider: (value: boolean) => void;
      setDisplayBottomDivider: (value: boolean) => void;
      tempDocuments: Map<string, TempDocument>;
      setTempDocuments: React.Dispatch<
        React.SetStateAction<Map<string, TempDocument>>
      >;
      readOnly?: boolean;
      flaggedComment?: string;
    },
    ref,
  ) {
    const formik = useFormikContext<FormType>();
    const addEntityFormId = useId();
    const [hideTaxId, setHideTaxId] = useState(true);
    const taxIdMaskProps = getSensitiveFieldMaskProps(hideTaxId);
    const { getTranslation } = useContext(I18nContext);
    const requiredDocumentsQuery = useRequiredKeyPartyDocumentsQuery(
      "entity",
      formik.values.keyParty.entityType,
    );
    const snackbar = useContext(SnackbarContext);
    const [documents, setDocuments] = useState<DocumentState[]>([]);

    const keyPartyDocumentsQuery = useGetKeyPartyDocumentsQuery(
      formik.values.keyParty.id,
    );
    const uploadInvestorDocumentTempMutation =
      useUploadInvestorDocumentTempMutation();
    const deleteKeyPartyDocumentMutation = useDeleteKeyPartyDocumentMutation();
    const queryClient = useQueryClient();

    useEffect(() => {
      if (requiredDocumentsQuery.data?.data && keyPartyDocumentsQuery.data) {
        const initialDocuments: DocumentState[] =
          requiredDocumentsQuery.data.data.map((document, index) => {
            const existingDoc = keyPartyDocumentsQuery.data.data.find(
              (d) => d.documentType === document.documentType,
            );

            if (existingDoc) {
              return {
                documentId: existingDoc.id,
                documentType: existingDoc.documentType,
                fileName: existingDoc.fileName,
                fileUrl: existingDoc.url,
                thumbnail: existingDoc.thumbnail,
                index,
                status: DocumentStatuses.UPLOADED,
              };
            }

            return {
              documentType: document.documentType,
              index,
              status: DocumentStatuses.PENDING,
            };
          });
        setDocuments(initialDocuments);
      }
    }, [requiredDocumentsQuery.data, keyPartyDocumentsQuery.data]);

    useEffect(() => {
      if (requiredDocumentsQuery.isError) {
        setDisplayTopDivider(false);
        setDisplayBottomDivider(false);
        snackbar.showSnackbar({
          message: (
            <ErrorSnackbar
              error={requiredDocumentsQuery.error as ResponseError}
            />
          ),
          severity: "error",
        });
        onClose();
      }
    }, [
      requiredDocumentsQuery.isError,
      requiredDocumentsQuery.error,
      setDisplayTopDivider,
      setDisplayBottomDivider,
      snackbar,
      onClose,
    ]);

    const updateDocumentState = (documentState: DocumentState) => {
      setDocuments((prevDocuments) => {
        const updatedDocuments = [...prevDocuments];
        updatedDocuments[documentState.index] = documentState;
        return updatedDocuments;
      });
    };

    const uploadDocument = async (
      file: File,
      documentType: string,
      index: number,
    ) => {
      try {
        const tempUploadResponse =
          await uploadInvestorDocumentTempMutation.mutateAsync({ file });

        // Store temp upload info
        setTempDocuments((prev) => {
          const newMap = new Map(prev);
          newMap.set(documentType, {
            documentType,
            fileName: tempUploadResponse.fileName,
            fileType: tempUploadResponse.fileType,
            url: tempUploadResponse.url,
            file,
          });
          return newMap;
        });

        // Update document state to show as uploaded (but not permanent yet)
        updateDocumentState({
          documentType,
          file,
          fileName: tempUploadResponse.fileName,
          index,
          status: DocumentStatuses.UPLOADED,
        });
      } catch (error) {
        snackbar.showSnackbar({
          severity: "error",
          message: <ErrorSnackbar error={error as ResponseError} />,
        });
      }
    };

    const removeDocument = async (
      documentType: string,
      index: number,
      documentId?: string,
    ) => {
      // If it's a temp document (not yet saved), just remove from temp state
      if (tempDocuments.has(documentType)) {
        setTempDocuments((prev) => {
          const newMap = new Map(prev);
          newMap.delete(documentType);
          return newMap;
        });
        updateDocumentState({
          documentType,
          index,
          status: DocumentStatuses.PENDING,
        });
        return;
      }

      if (!documentId) {
        throw new Error("Unreachable.");
      }

      // Otherwise, delete from permanent storage
      await deleteKeyPartyDocumentMutation.mutateAsync(
        { keyPartyId: formik.values.keyParty.id, documentId },
        {
          onError: (error) => {
            snackbar.showSnackbar({
              severity: "error",
              message: <ErrorSnackbar error={error} />,
            });
          },
          onSuccess: () => {
            updateDocumentState({
              documentType,
              index,
              status: DocumentStatuses.PENDING,
            });
            Promise.all([
              invalidateKeyPartiesQueries(queryClient),
              queryClient.invalidateQueries({
                queryKey: ["key-party-documents", formik.values.keyParty.id],
              }),
            ])
              .then(() => {
                snackbar.showSnackbar({
                  severity: "info",
                  message: (
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_REMOVE_SUCCESS
                      }
                      replacements={{
                        documentType: getTranslation(
                          getDocumentTypeTranslationKeys(documentType).name,
                        ),
                      }}
                    />
                  ),
                });
              })
              .catch((e) => {
                snackbar.showSnackbar({
                  severity: "error",
                  message: <ErrorSnackbar error={e as ResponseError} />,
                });
              });
          },
        },
      );
    };

    return (
      <>
        <WhmDialogTitle variant="h4">
          <Translate
            translationKey={
              translationKeys.PROFILE_KEY_PARTIES_COMPLETE_DETAILS_TITLE
            }
          />
        </WhmDialogTitle>
        <WhmIconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: "8px",
            top: "8px",
          }}
        >
          <CloseIcon />
        </WhmIconButton>
        {displayTopDivider && <WhmDivider />}
        <WhmDialogContent
          ref={ref}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, offsetHeight } = e.currentTarget;

            setDisplayTopDivider(scrollTop !== 0);
            setDisplayBottomDivider(scrollTop !== scrollHeight - offsetHeight);
          }}
        >
          {requiredDocumentsQuery.isPending ||
          requiredDocumentsQuery.isError ||
          keyPartyDocumentsQuery.isPending ? (
            <RequiredDocumentsSkeleton />
          ) : (
            <Form id={addEntityFormId}>
              <WhmStack gap="16px">
                {flaggedComment && (
                  <WhmAlert icon={false} severity="warning">
                    {flaggedComment}
                  </WhmAlert>
                )}
                <WhmTypography>
                  <TranslateHtml
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_COMPLETE_DETAILS_SUBTITLE
                    }
                    replacements={{
                      name: formik.values.keyParty.displayName || "",
                    }}
                  />
                </WhmTypography>
                <WhmStack>
                  <WhmTypography variant="subtitle1">
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_BUSINESS_INFORMATION
                      }
                    />
                  </WhmTypography>
                  <WhmTypography variant="body2" color="text.secondary">
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_BUSINESS_INFORMATION_SUBTITLE
                      }
                    />
                  </WhmTypography>
                </WhmStack>
                <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
                  <WhmGrid item xs={6}>
                    <CountryTextField
                      name="keyParty.taxCountryCode"
                      fullWidth
                      value={formik.values.keyParty.taxCountryCode}
                      label={
                        <Translate
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_TAX_RESIDENCE_COUNTRY
                          }
                        />
                      }
                      fieldError={
                        formik.touched.keyParty?.taxCountryCode &&
                        Boolean(formik.errors.keyParty?.taxCountryCode)
                      }
                      helperText={
                        formik.touched.keyParty?.taxCountryCode &&
                        formik.errors.keyParty?.taxCountryCode
                      }
                      onChange={(e, v) => {
                        void formik.setFieldValue(
                          "keyParty.taxCountryCode",
                          v?.value ?? "",
                        );
                      }}
                      onBlur={() => {
                        void formik.setFieldTouched("keyParty.taxCountryCode");
                      }}
                      disabled={readOnly || formik.isSubmitting}
                    />
                  </WhmGrid>
                  <WhmGrid item xs={6}>
                    <WhmStack direction="row" spacing="8px">
                      <FormikWhmTextField
                        sx={{ margin: 0, ...taxIdMaskProps.sx }}
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_TAX_ID
                            }
                          />
                        }
                        fullWidth
                        name="keyParty.taxId"
                        type={taxIdMaskProps.type}
                        autoComplete={taxIdMaskProps.autoComplete}
                        disabled={readOnly || formik.isSubmitting}
                        helperText={
                          formik.touched.keyParty?.taxId &&
                          formik.errors.keyParty?.taxId
                        }
                        autoTrim
                        InputProps={{
                          endAdornment: (
                            <WhmInputAdornment position="end">
                              {hideTaxId ? (
                                <WhmIconButton
                                  onClick={() => setHideTaxId(false)}
                                  size="small"
                                >
                                  <ClosedEye
                                    width="20"
                                    height="20"
                                    fill={whmTheme.palette.action.active}
                                  />
                                </WhmIconButton>
                              ) : (
                                <WhmIconButton
                                  onClick={() => setHideTaxId(true)}
                                  size="small"
                                >
                                  <OpenEye
                                    width="20"
                                    height="20"
                                    fill={whmTheme.palette.action.active}
                                  />
                                </WhmIconButton>
                              )}
                            </WhmInputAdornment>
                          ),
                        }}
                      />
                      <WhmBox sx={{ paddingTop: "18px" }}>
                        <WhmTooltip
                          title={getTranslation(
                            translationKeys.PROFILE_KEY_PARTIES_TAX_PURPOSE_INFO,
                          )}
                        >
                          <InfoIcon
                            width="20"
                            height="20"
                            fill={whmTheme.palette.action.active}
                          />
                        </WhmTooltip>
                      </WhmBox>
                    </WhmStack>
                  </WhmGrid>
                  <WhmGrid item xs={6}>
                    <FormikWhmTextField
                      sx={{ margin: 0 }}
                      label={
                        <Translate
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_ENTITY_CONTACT_EMAIL
                          }
                        />
                      }
                      fullWidth
                      name="keyParty.email"
                      disabled={readOnly || formik.isSubmitting}
                    />
                  </WhmGrid>
                  <WhmGrid item xs={6}>
                    <WhmStack direction="row" spacing="8px">
                      <FormikWhmTextField
                        sx={{ margin: 0 }}
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_DOING_BUSINESS_AS
                            }
                          />
                        }
                        fullWidth
                        name="keyParty.doingBusinessAs"
                        disabled={readOnly || formik.isSubmitting}
                      />
                      <WhmBox sx={{ paddingTop: "18px" }}>
                        <WhmTooltip
                          title={getTranslation(
                            translationKeys.PROFILE_ENTITY_INFORMATION_ENTITY_INFORMATION_CARD_DBA_TOOLTIP,
                          )}
                        >
                          <InfoIcon
                            width="20"
                            height="20"
                            fill={whmTheme.palette.action.active}
                          />
                        </WhmTooltip>
                      </WhmBox>
                    </WhmStack>
                  </WhmGrid>
                </WhmGrid>
                {requiredDocumentsQuery.data.meta.total > 0 && (
                  <RequiredDocuments
                    requiredDocuments={requiredDocumentsQuery.data.data}
                    documents={documents}
                    onUpload={uploadDocument}
                    onDelete={removeDocument}
                    readOnly={readOnly}
                  />
                )}
              </WhmStack>
            </Form>
          )}
        </WhmDialogContent>
        {displayBottomDivider && <WhmDivider />}
        <WhmDialogActions>
          <WhmButton
            variant="outlined"
            color="secondary"
            onClick={() => {
              formik.resetForm();
              onClose();
            }}
          >
            <Translate
              translationKey={translationKeys.PROFILE_KEY_PARTIES_CANCEL}
            />
          </WhmButton>
          {!readOnly && (
            <WhmLoadingButton
              variant="contained"
              type="submit"
              form={addEntityFormId}
              disabled={
                formik.isSubmitting ||
                requiredDocumentsQuery.isPending ||
                keyPartyDocumentsQuery.isPending ||
                uploadInvestorDocumentTempMutation.isPending
              }
              loading={formik.isSubmitting}
              sx={{
                svg: {
                  display: "block !important",
                },
                "&.MuiLoadingButton-loading": {
                  color: "black",
                  opacity: "0.3",
                  span: {
                    color: "black",
                  },
                },
              }}
            >
              {formik.isSubmitting ? (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUBMIT_IN_PROGRESS
                  }
                />
              ) : (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUBMIT
                  }
                />
              )}
            </WhmLoadingButton>
          )}
        </WhmDialogActions>
      </>
    );
  },
);

function RequiredDocuments({
  requiredDocuments,
  documents,
  onUpload,
  onDelete,
  readOnly = false,
}: {
  requiredDocuments: {
    documentType: string;
  }[];
  documents: DocumentState[];
  onUpload: (file: File, documentType: string, index: number) => Promise<void>;
  onDelete: (
    documentType: string,
    index: number,
    documentId?: string,
  ) => Promise<void>;
  readOnly?: boolean;
}) {
  const { getTranslation } = useContext(I18nContext);

  return (
    <>
      <WhmDivider />
      <WhmStack>
        <WhmTypography variant="subtitle1">
          <Translate
            translationKey={translationKeys.PROFILE_KEY_PARTIES_LEGAL_DOCUMENTS}
          />
        </WhmTypography>
        <WhmTypography variant="body2" color="text.secondary">
          <Translate
            translationKey={
              translationKeys.PROFILE_KEY_PARTIES_LEGAL_DOCUMENTS_SUBTITLE
            }
          />
        </WhmTypography>
      </WhmStack>
      <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
        {requiredDocuments.map((doc, index) => {
          const { name, description } = getDocumentTypeTranslationKeys(
            doc.documentType,
          );
          const documentState = documents.find(
            (d) => d.documentType === doc.documentType,
          );
          return (
            <WhmGrid key={doc.documentType} item xs={6}>
              <WhmStack gap="8px" justifyContent="space-between" height="100%">
                <WhmStack>
                  <WhmTypography>
                    <Translate translationKey={name} />
                  </WhmTypography>
                  <WhmTypography variant="body2" color="text.secondary">
                    <Translate translationKey={description} />
                  </WhmTypography>
                </WhmStack>
                <WhmDropzone
                  disabled={readOnly}
                  title={getTranslation(name)}
                  preview={
                    documentState?.fileName
                      ? {
                          url: documentState.fileUrl ?? "",
                          fileName: documentState.fileName,
                        }
                      : undefined
                  }
                  onValidate={(file: File) => {
                    if (file.size > 50 * 1024 * 1024) {
                      return {
                        error: getTranslation(
                          translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_INVALID_SIZE_ERROR,
                        ),
                      };
                    }
                    return undefined;
                  }}
                  hideCancelButtonWhenLoading
                  uploadButtonText={getTranslation(
                    translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA,
                  )}
                  description={getTranslation(
                    translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_SUBTITLE,
                  )}
                  deleteButtonText={getTranslation(
                    translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA_REMOVE,
                  )}
                  uploadingCancelText={getTranslation(
                    translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA_CANCEL,
                  )}
                  onUpload={async (file: File) => {
                    await onUpload(file, doc.documentType, index);
                  }}
                  onDelete={async () => {
                    await onDelete(
                      doc.documentType,
                      index,
                      documentState?.documentId,
                    );
                  }}
                  required
                  uploadingErrorText={documentState?.errorMessage}
                />
              </WhmStack>
            </WhmGrid>
          );
        })}
      </WhmGrid>
    </>
  );
}

function RequiredDocumentsSkeleton() {
  return (
    <WhmBox>
      <WhmStack gap="16px" minWidth="600px">
        <WhmSkeleton variant="rounded" height={24} />
        <WhmSkeleton variant="rounded" height={48} />
        <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
          {[1, 2, 3, 4].map((i) => (
            <WhmGrid key={i} item xs={6}>
              <WhmSkeleton variant="rectangular" width="100%" height={56} />
            </WhmGrid>
          ))}
        </WhmGrid>
        <WhmDivider />
        <WhmSkeleton variant="rounded" height={48} />
        <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
          {[1, 2, 3, 4].map((i) => (
            <WhmGrid key={i} item xs={6}>
              <WhmSkeleton variant="rectangular" width="100%" height={166} />
            </WhmGrid>
          ))}
        </WhmGrid>
      </WhmStack>
    </WhmBox>
  );
}
